export const PLAN_DRAFTING_STAGE_MS = 3200
export const PLAN_DRAFTING_LONG_WAIT_MS = 12000

export const PLAN_DRAFTING_STAGES = [
    'Reading your goal',
    'Sketching the path',
    'Sizing the first steps',
    'Checking the timeframe',
] as const

export type TPlanDraftingStage = typeof PLAN_DRAFTING_STAGES[number]
export type TPlanDraftingMotionMode = 'animated' | 'calm'

export interface TPlanDraftingStatus {
    elapsedMs: number
    stageIndex: number
    stageLabel: TPlanDraftingStage
    completedStageCount: number
    isLongWait: boolean
    message: string
}

export const planDraftingMotionMode = (prefersReducedMotion: boolean): TPlanDraftingMotionMode =>
    prefersReducedMotion ? 'calm' : 'animated'

export const getPlanDraftingStatus = (elapsedMs: number): TPlanDraftingStatus => {
    const safeElapsed = Math.max(0, elapsedMs)
    const rawStageIndex = Math.floor(safeElapsed / PLAN_DRAFTING_STAGE_MS)
    const stageIndex = rawStageIndex % PLAN_DRAFTING_STAGES.length
    const isLongWait = safeElapsed >= PLAN_DRAFTING_LONG_WAIT_MS

    return {
        elapsedMs: safeElapsed,
        stageIndex,
        stageLabel: PLAN_DRAFTING_STAGES[stageIndex],
        completedStageCount: isLongWait
            ? PLAN_DRAFTING_STAGES.length
            : Math.min(rawStageIndex, PLAN_DRAFTING_STAGES.length),
        isLongWait,
        message: isLongWait ? 'Taking longer than usual. Still working.' : PLAN_DRAFTING_STAGES[stageIndex],
    }
}

const nextPlanDraftingUpdateDelay = (elapsedMs: number) => {
    if (elapsedMs >= PLAN_DRAFTING_LONG_WAIT_MS) return null

    const nextStageAt = (Math.floor(elapsedMs / PLAN_DRAFTING_STAGE_MS) + 1) * PLAN_DRAFTING_STAGE_MS
    const nextUpdateAt = Math.min(nextStageAt, PLAN_DRAFTING_LONG_WAIT_MS)
    return Math.max(0, nextUpdateAt - elapsedMs)
}

export const startPlanDraftingClock = (
    onTick: (status: TPlanDraftingStatus) => void,
    now: () => number = () => Date.now()
) => {
    const startedAt = now()
    let timeout: ReturnType<typeof globalThis.setTimeout> | undefined

    const tick = () => {
        const elapsedMs = Math.max(0, now() - startedAt)
        const status = getPlanDraftingStatus(elapsedMs)
        onTick(status)

        const delay = nextPlanDraftingUpdateDelay(elapsedMs)
        if (delay != null) timeout = globalThis.setTimeout(tick, delay)
    }

    tick()

    return () => {
        if (timeout !== undefined) globalThis.clearTimeout(timeout)
    }
}
